import { useCallback, useEffect, useRef } from 'react'
import { useMutation } from '@apollo/client/react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'

import { useCloseModal } from '@/Router'
import { StepTransitions } from '@/components/ui/StepTransitions'
import Button from '@/components/ui/Button'
import Loading from '@/components/ui/Loading'
import { DynamicStepComponent } from './DynamicStepComponent'
import { useConnectorSteps } from '../hooks/useConnectorSteps'
import { useConnector } from '../hooks/useConnector'
import { showToast, ToastType } from '@/features/toast'

function ConnectorForms({
  connector,
  refetch
}: {
  connector: GraphQL.IntegrationConnectorFragmentFragment
  refetch: () => void
}) {
  const steps = useConnectorSteps(
    connector.provider,
    connector.connectionData ? JSON.parse(connector.connectionData) : undefined
  )

  const [update] = useMutation(AdminAPI.integration.FINISH_CONNECTOR, {
    refetchQueries: [AdminAPI.integration.GET_ALL_FEATURES],
    update(cache, { data, errors }) {
      if (errors) return
      if (!data?.finishStepIntegrationConnector) return

      cache.writeQuery({
        query: AdminAPI.integration.GET_CONNECTOR,
        variables: { provider: connector.provider },
        data: {
          getIntegrationConnector: getFragment(
            SharedAPI.integration.CONNECTOR_FRAGMENT,
            data.finishStepIntegrationConnector
          )
        }
      })
    }
  })

  const onSubmit = useCallback(
    (data: object) => {
      return update({
        variables: {
          provider: connector.provider,
          data: JSON.stringify(data)
        }
      }).then(({ error }) => {
        if (error) return Promise.reject(error)
        return Promise.resolve()
      })
    },
    [connector, update]
  )

  return (
    <StepTransitions
      steps={steps.map(step => ({
        Component: DynamicStepComponent({ step, onSubmit, onReload: refetch }),
        label: step.title,
        icon: step.icon,
        value: step.id
      }))}
      currentStep={connector.step}
    />
  )
}

export function ConnectorSteps({ provider }: { provider: GraphQL.Providers }) {
  const { t } = useLingui()
  const closeModal = useCloseModal()
  const isCreatingRef = useRef(false)
  const { connector: currentConnector, refetch } = useConnector(provider)

  const [create] = useMutation(AdminAPI.integration.CREATE_CONNECTOR, {
    refetchQueries: [AdminAPI.integration.GET_ALL_FEATURES],
    update(cache, { data, errors }) {
      if (errors) return
      if (!data?.createIntegrationConnector) return

      cache.writeQuery({
        query: AdminAPI.integration.GET_CONNECTOR,
        variables: { provider },
        data: {
          getIntegrationConnector: getFragment(
            SharedAPI.integration.CONNECTOR_FRAGMENT,
            data.createIntegrationConnector
          )
        }
      })
    }
  })

  const [remove, { loading }] = useMutation(
    AdminAPI.integration.CANCEL_CONNECTOR,
    {
      refetchQueries: [AdminAPI.integration.GET_ALL_FEATURES],
      update(cache) {
        cache.evict({
          id: cache.identify({
            __typename: 'IntegrationConnector',
            provider
          })
        })
        cache.gc()
      },
      onCompleted: closeModal
    }
  )

  useEffect(() => {
    if (currentConnector) return
    if (isCreatingRef.current) return

    isCreatingRef.current = true

    create({
      variables: {
        provider
      }
    })
      .then(() => null)
      .catch(() => {
        showToast({
          id: 'unknown-error',
          type: ToastType.ERROR,
          message: t`There was an unexpected issue creating the connector. Please write our support if this keeps happening.`
        })

        closeModal()
      })
      .finally(() => {
        isCreatingRef.current = false
      })
  }, [currentConnector, provider, create, t, closeModal])

  if (!currentConnector) {
    return (
      <div className="flex flex-row justify-center items-center h-20 w-full">
        <Loading />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      <ConnectorForms connector={currentConnector} refetch={refetch} />
      <div className="flex flex-row justify-start">
        <Button
          onClick={() => remove({ variables: { provider } })}
          loading={loading}
        >
          <Trans>Cancel</Trans>
        </Button>
      </div>
    </div>
  )
}
